import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Avatar,
  LinearProgress,
  Snackbar,
} from '@mui/material';
import {
  Close,
  Videocam,
  VideocamOff,
  LockOpen,
  Lock,
  Refresh,
  Security,
  Warning,
} from '@mui/icons-material';
import apiClient from '../../utils/axios';

const CameraControl = ({ open, onClose }) => {
  const [cameras, setCameras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [operationLoading, setOperationLoading] = useState({});
  const [notification, setNotification] = useState({ 
    open: false, 
    message: '', 
    severity: 'info' 
  });

  useEffect(() => {
    if (open) {
      loadCameras();
    }
  }, [open]);

  const loadCameras = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/vehiculos/cameras');
      console.log('Response completo:', response);
      
      if (response.status === 200) {
        // Tu estructura: response.data.data contiene el array de cámaras
        const camerasData = response.data.data || [];
        
        // Mapear activa -> status para compatibilidad con el componente
        const adaptedCameras = camerasData.map(camera => ({
          ...camera,
          status: camera.activa ? 'online' : 'offline'
        }));
        
        setCameras(adaptedCameras);
        console.log(`Cargadas ${adaptedCameras.length} cámaras:`, adaptedCameras);
      } else {
        throw new Error(response.data?.message || 'Error cargando cámaras');
      }
    } catch (error) {
      console.error('Error cargando cámaras:', error);
      showNotification('Error cargando sistema de cámaras: ' + (error.message || error), 'error');
      setCameras([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGateOperation = async (cameraName, operation) => {
    const operationKey = `${cameraName}_${operation}`;
    setOperationLoading(prev => ({ ...prev, [operationKey]: true }));
    
    try {
      const endpoint = operation === 'open' 
        ? `/api/vehiculos/cameras/${encodeURIComponent(cameraName)}/open`
        : `/api/vehiculos/cameras/${encodeURIComponent(cameraName)}/close`;
      
      const response = await apiClient.post(endpoint);

      if (response.status === 200) {
        // Actualizar estado de la cámara localmente
        setCameras(prev => prev.map(camera => 
          camera.name === cameraName 
            ? { 
                ...camera, 
                gateStatus: operation === 'open' ? 'opening' : 'closing'
              }
            : camera
        ));

        // Simular cambio de estado después de la operación
        setTimeout(() => {
          setCameras(prev => prev.map(camera => 
            camera.name === cameraName 
              ? { 
                  ...camera, 
                  gateStatus: operation === 'open' ? 'open' : 'closed'
                }
              : camera
          ));
        }, 3000);

        const actionText = operation === 'open' ? 'abierta' : 'cerrada';
        showNotification(`Barrera ${actionText} exitosamente: ${cameraName}`, 'success');
        
      } else {
        throw new Error(response.message || `Error ${operation === 'open' ? 'abriendo' : 'cerrando'} barrera`);
      }
      
    } catch (error) {
      console.error(`Error en operación ${operation}:`, error);
      const actionText = operation === 'open' ? 'abrir' : 'cerrar';
      showNotification(`Error al ${actionText} barrera ${cameraName}: ${error.message}`, 'error');
    } finally {
      setOperationLoading(prev => ({ ...prev, [operationKey]: false }));
    }
  };

  const showNotification = (message, severity = 'info') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  const renderCameraCard = (camera) => {
    const isOnline = camera.status === 'online';
    const openLoading = operationLoading[`${camera.name}_open`];
    const closeLoading = operationLoading[`${camera.name}_close`];

    return (
      <Grid item xs={12} sm={6} md={6} lg={4} key={camera.id}>
        <Card 
          elevation={2}
          sx={{ 
            borderRadius: 2,
            border: `2px solid ${isOnline ? '#6c757d' : '#dc3545'}`,
            backgroundColor: '#ffffff',
            transition: 'all 0.2s ease',
            '&:hover': {
              boxShadow: '0 4px 20px rgba(108, 117, 125, 0.15)',
              transform: 'translateY(-2px)'
            }
          }}
        >
          {/* Header */}
          <Box sx={{ 
            p: 2, 
            borderBottom: '1px solid #e9ecef',
            backgroundColor: isOnline ? '#f8f9fa' : '#f5c6cb'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ 
                  width: 32, 
                  height: 32, 
                  bgcolor: isOnline ? '#6c757d' : '#dc3545' 
                }}>
                  {isOnline ? <Videocam sx={{ fontSize: 18 }} /> : <VideocamOff sx={{ fontSize: 18 }} />}
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} color="#495057">
                    {camera.name}
                  </Typography>
                  <Typography variant="caption" color="#6c757d">
                    {camera.location}
                  </Typography>
                </Box>
              </Box>
              
              <Chip 
                label={isOnline ? 'ACTIVA' : 'INACTIVA'}
                size="small"
                sx={{ 
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  bgcolor: isOnline ? '#d4edda' : '#f8d7da',
                  color: isOnline ? '#155724' : '#721c24',
                  border: 'none'
                }}
              />
            </Box>
          </Box>

          {/* Vista de cámara simulada */}
          <CardContent sx={{ p: 2, pb: 1 }}>
            {/* Información adicional */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="#6c757d" display="block">
                TIPO: {camera.type?.toUpperCase()}
              </Typography>
            </Box>

            {/* Controles */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <IconButton 
                size="small" 
                onClick={loadCameras}
                disabled={loading}
                sx={{ 
                  color: '#6c757d',
                  '&:hover': { backgroundColor: '#f8f9fa' }
                }}
              >
                <Refresh sx={{ fontSize: 18 }} />
              </IconButton>

              {(
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant={camera.gateStatus === 'open' ? 'outlined' : 'contained'}
                    startIcon={openLoading ? <CircularProgress size={14} /> : <LockOpen sx={{ fontSize: 16 }} />}
                    onClick={() => handleGateOperation(camera.name, 'open')}
                    disabled={openLoading || closeLoading || camera.gateStatus === 'opening'}
                    sx={{ 
                      minWidth: '70px',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      bgcolor: camera.gateStatus === 'open' ? 'transparent' : '#28a745',
                      color: camera.gateStatus === 'open' ? '#28a745' : 'white',
                      borderColor: '#28a745',
                      '&:hover': {
                        bgcolor: camera.gateStatus === 'open' ? '#28a74510' : '#218838'
                      }
                    }}
                  >
                    ABRIR
                  </Button>
                  
                  <Button
                    size="small"
                    variant={camera.gateStatus === 'closed' ? 'outlined' : 'contained'}
                    startIcon={closeLoading ? <CircularProgress size={14} /> : <Lock sx={{ fontSize: 16 }} />}
                    onClick={() => handleGateOperation(camera.name, 'close')}
                    disabled={openLoading || closeLoading || camera.gateStatus === 'closing'}
                    sx={{ 
                      minWidth: '70px',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      bgcolor: camera.gateStatus === 'closed' ? 'transparent' : '#6c757d',
                      color: camera.gateStatus === 'closed' ? '#6c757d' : 'white',
                      borderColor: '#6c757d',
                      '&:hover': {
                        bgcolor: camera.gateStatus === 'closed' ? '#6c757d10' : '#5a6268'
                      }
                    }}
                  >
                    CERRAR
                  </Button>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 2,
            minHeight: '80vh',
            backgroundColor: '#fafafa'
          }
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: '#495057',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Security sx={{ fontSize: 24 }} />
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Centro de Seguridad y Control
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Gestión de accesos vehiculares y monitoreo en tiempo real
              </Typography>
            </Box>
          </Box>
          <IconButton 
            onClick={onClose}
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3, backgroundColor: '#fafafa' }}>
          {loading && <LinearProgress sx={{ mb: 3, borderRadius: 1 }} />}

          {/* Grid de cámaras */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress size={40} sx={{ mb: 2, color: '#6c757d' }} />
                <Typography variant="body1" color="#6c757d">
                  Conectando con sistema de cámaras...
                </Typography>
              </Box>
            </Box>
          ) : cameras.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Security sx={{ fontSize: 64, color: '#6c757d', mb: 2 }} />
              <Typography variant="h6" color="#6c757d" gutterBottom>
                No hay cámaras configuradas
              </Typography>
              <Typography variant="body2" color="#6c757d">
                Configure las cámaras ANPR en el sistema para usar esta funcionalidad
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3} m={2}>
              {cameras.map(renderCameraCard)}
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, backgroundColor: 'white', borderTop: '1px solid #e9ecef' }}>
          <Button 
            onClick={loadCameras}
            startIcon={<Refresh />}
            disabled={loading}
            sx={{ color: '#6c757d' }}
          >
            Actualizar Sistema
          </Button>
          <Button 
            onClick={onClose}
            variant="contained"
            sx={{ 
              backgroundColor: '#495057',
              '&:hover': { backgroundColor: '#3d4549' }
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notificaciones */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={closeNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={closeNotification} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CameraControl;